import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { SEO } from "@/shared/components/SEO";
import { trackViewContent, trackSchedule } from "@/shared/utils/metaPixel";
import { 
  BarChart3, 
  Play, 
  Users, 
  Edit, 
  Calendar,
  ArrowRight,
  Camera,
  FileText,
  FolderOpen,
  Video,
  MessageCircle,
  FileCheck
} from "lucide-react";

export default function FeaturesPage() {
  const navigate = useNavigate();

  // Track ViewContent for Meta Pixel on page load
  useEffect(() => {
    trackViewContent({
      contentName: 'Features Page',
      contentCategory: 'Product',
    });
  }, []);

  const handleGetStarted = () => {
    navigate('/auth?mode=signup', { state: { from: '/features' } });
  };

  const handleRequestDemo = () => {
    // Track Schedule event for Meta Pixel (demo scheduling intent)
    trackSchedule({
      email: '', // Anonymous user
    }, 'Demo Request');
    window.open('https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn', '_blank');
  };

  return (
    <>
      <SEO
        title="Features - Wlkthru Video Calling Platform"
        description="Discover wlkthru.co features: HD video calling, screen sharing, session recording, AI-powered summaries, scheduling, and more. Built for contractors and service professionals."
        canonical="https://www.wlkthru.co/features"
        keywords="wlkthru features, wlkthru.co capabilities, video calling features, contractor tools, remote inspection features, session recording"
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left side navigation */}
            <nav className="flex items-center space-x-8 ml-1 md:ml-0">
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                className="text-xs md:text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
                wlkthru
              </button>
              <div className="hidden md:flex space-x-8">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium text-primary"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </div>
              {/* Mobile navigation - next to logo */}
              <div className="md:hidden flex space-x-3">
                <button
                  onClick={() => {
                    navigate('/features');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium text-primary"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </div>
            </nav>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => navigate('/auth')}
                className="text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <button 
                onClick={() => navigate('/auth')}
                className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
              <button 
                onClick={handleGetStarted}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors cursor-pointer"
              >
                Get started for free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight mb-8 animate-slide-in-right">
                <span className="text-foreground">Offer your customers a video call first</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed mb-12">
                Save time and cut costs with video tools that streamline your customer interactions and improve efficiency.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={handleGetStarted}
                    variant="cta-primary"
                    size="lg"
                    className="text-lg px-6 py-3"
                  >
                    Start free trial
                  </Button>
                <Button onClick={handleRequestDemo} variant="cta-outline" size="lg" className="text-lg px-8 py-4">
                  Request Demo
                </Button>
              </div>
            </div>

            {/* Right Content - Feature Images */}
            <div className="relative flex items-center justify-center">
              {/* Background decorative circles */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-secondary/20 rounded-full blur-2xl"></div>

              {/* Main feature image - responsive sizing */}
              <div className="relative z-10 rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300 w-32 sm:w-40 md:w-48 lg:w-1/2">
                <img
                  src="/Features Image 1.png"
                  alt="wlkthru features overview"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Secondary floating image - responsive sizing */}
              <div className="absolute -bottom-8 -left-8 z-20 rounded-xl shadow-lg overflow-hidden transform -rotate-3 hover:rotate-0 transition-transform duration-300 w-24 sm:w-32 md:w-48 lg:w-72">
                <img
                  src="/Features Image 2.png"
                  alt="wlkthru feature detail"
                  className="w-full h-auto object-contain"
                />
              </div>

              {/* Third floating image - responsive sizing */}
              <div className="absolute -top-8 -right-8 z-15 rounded-lg shadow-lg overflow-hidden transform rotate-6 hover:rotate-3 transition-transform duration-300 w-20 sm:w-28 md:w-40 lg:w-64">
                <img
                  src="/Features Image 3.png"
                  alt="wlkthru feature highlight"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Features of <span className="text-primary">wlkthru</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Video className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Instant Video Calls</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Easily send customers a text with a link they can join instantly—no app required.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Smart Scheduling</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Conveniently schedule your customer calls-just like you would with in person visits.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Play className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Request Videos</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Request videos from customers to see exactly what they need, speeding up quotes and service.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">In Call Chat</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Talking's great—but sometimes you just need to write it down. Use in-call chat to share notes, instructions, or anything else that's easier to text.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Edit className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">2D Animations & Writing</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Draw, annotate, and create visuals directly on screen during video calls.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Users className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Customer Profiles & Projects</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Organize customers and group jobs under projects.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Camera className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Snapshot</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Capture important moments during calls for documentation, estimates, and follow-up reference.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Invoicing</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Send invoices and get paid faster. Bill customers directly, track payments, and even charge transaction fees so you receive the full amount billed.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <FileCheck className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-semibold">Automatic Session Summary</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Get a post call summary with key moments as well as AI-powered full transcripts.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Ready to transform your customer experience?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of companies using wlkthru to improve their customer experience and boost conversions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleGetStarted}
                  variant="cta-primary"
                  size="lg"
                  className="text-lg px-6 py-3"
                >
                  Start free trial
                </Button>
              <Button 
                onClick={() => {
                  navigate('/pricing');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }} 
                variant="cta-outline" 
                size="lg" 
                className="text-lg px-6 py-4"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 pb-6 bg-primary text-center md:text-left">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
            {/* Brand Section */}
            <div className="space-y-4">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-4xl font-bold hover:text-white/80 transition-colors cursor-pointer"
              >
                wlkthru
              </button>
              <div className="w-16 h-0.5 bg-white/30 mx-auto md:mx-0"></div>
              <div className="space-y-3">
                <p className="text-white/90">Check us out on:</p>
                <div className="flex gap-2 justify-center">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578600642587" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">f</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/wlkthru.co/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">ig</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/wlkthruco/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">in</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@wlkthruco" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">yt</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Get in Touch and Legal */}
            <div className="flex flex-col md:flex-row gap-8 md:col-span-2 justify-end">
              {/* Get in Touch */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
                <ul className="space-y-2">
                  <li><a href="/customer-support" className="text-white/80 hover:text-white transition-colors">• Customer Support</a></li>
                  <li><a href="https://api.leadconnectorhq.com/widget/booking/9yoGqzJkpywxCy012LYn" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors">• Demo Request</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Legal</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => { navigate('/legal?section=privacy'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Privacy Policy</button></li>
                  <li><button onClick={() => { navigate('/legal?section=terms'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Terms and Conditions</button></li>
                  <li><button onClick={() => { navigate('/legal?section=disclaimer'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Disclaimer</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-2 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs font-light">
              Copyright © 2025 wlkthru | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}